import { FC, RefObject, useCallback, useRef, useState } from "react";

import {
  getSweepstakesPrizeValueRestriction,
  Locales,
  sumSweepstakesPrizeValues,
  TicketingFormCategory,
} from "@simplyk/common";
import { useForm } from "react-hook-form";

import { useFormSaveAndNavigate } from "../useFormSaveAndNavigate";

import { buildEmptyPrize, toPrizeFormData, toPrizeInput } from "./helpers/prizeFormData";
import { PrizeValueRestriction, StepPrizesFormData } from "./helpers/types";
import { usePrizeList } from "./helpers/usePrizeList";
import { StepPrizes } from "./StepPrizes";

import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { ImageUploaderHandle } from "@/components/ImageUploader/types";
import { useScrollPreviewWithArrow } from "@/features/LiveFormEditor/AnimatePreview/useScrollPreviewWithArrow";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { useUpdateTicketingDataInPreview } from "@/features/LiveFormEditor/common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";
import { useLiveFormEditorExit } from "@/features/LiveFormEditor/Navigation/useLiveFormEditorExit";

type PrizeInput = ReturnType<typeof toPrizeInput>;

type TicketingPreviewWithPrizes = {
  prizes?: Array<
    Omit<PrizeInput, "title" | "description"> & {
      prizeFields: Array<Pick<PrizeInput, "title" | "description"> & { locale: Locales }>;
    }
  >;
};

export const TicketingLiveEditorStepPrizes: FC = () => {
  const { t } = useTranslate();
  const { organization } = useCurrentUserContext();
  const {
    apiFormObject: apiTicketing,
    updateApiFormObject,
    stepProgress,
    currentStepIndex,
    setPreviewPrizeFocusId,
  } = useLiveFormEditorContext<"ticketing">();
  const { exitLiveFormEditor } = useLiveFormEditorExit();
  const { scrollAndUpdateArrow } = useScrollPreviewWithArrow();

  const savedPrizes = apiTicketing.prizes ?? [];
  const isFirstVisit = savedPrizes.length === 0 && stepProgress.saved < currentStepIndex;
  const [defaultValues] = useState<StepPrizesFormData>(() => ({
    prizes: isFirstVisit
      ? [buildEmptyPrize(t("dashboard", "liveFormEditor.stepPrizes.defaultPrizeTitle"))]
      : savedPrizes.map((prize) => toPrizeFormData(prize, apiTicketing.locale)),
    isImageUploading: false,
  }));

  const form = useForm<StepPrizesFormData>({ defaultValues });

  const uploadersRef = useRef(new Map<string, RefObject<ImageUploaderHandle | null>>());
  const handleUploaderChange = useCallback(
    (prizeId: string, uploaderRef: RefObject<ImageUploaderHandle | null> | null) => {
      if (uploaderRef) {
        uploadersRef.current.set(prizeId, uploaderRef);
      } else {
        uploadersRef.current.delete(prizeId);
      }
    },
    []
  );
  const finalizePendingImage = async (prizeId: string) => {
    await uploadersRef.current.get(prizeId)?.current?.finalizeSquareCrop();
  };

  const prizeList = usePrizeList(
    form,
    isFirstVisit ? (defaultValues.prizes[0]?.id ?? null) : null,
    finalizePendingImage
  );
  const [valueRestriction, setValueRestriction] = useState<PrizeValueRestriction | null>(null);

  const { mutateAsync: updateTicketingPrizes } = trpc.updateTicketingPrizes.useMutation();

  useUpdateTicketingDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      (liveDataToMutate.ticketing as TicketingPreviewWithPrizes).prizes = formData.prizes.map((prize) => {
        const { title, description, ...prizeInput } = toPrizeInput(prize);
        return { ...prizeInput, prizeFields: [{ locale: apiTicketing.locale, title, description }] };
      });
    },
  });

  const { handleSaveAndNext, handleSaveAndBack } = useFormSaveAndNavigate<StepPrizesFormData>({
    type: "ticketing",
    form,
    alwaysSaveOnNext: isFirstVisit,
    alwaysSaveOnBack: isFirstVisit,
    saveData: async () => {
      await Promise.all([...uploadersRef.current.keys()].map(finalizePendingImage));
      const { data, error } = await updateTicketingPrizes({
        ticketingId: apiTicketing.id,
        prizes: form.getValues("prizes").map(toPrizeInput),
      });
      if (data) {
        updateApiFormObject({ prizes: data });
      }
      return { data: null, error };
    },
    errorMessage: t("dashboard", "liveFormEditor.stepPrizes.error"),
    onInvalid: prizeList.expandFirstInvalidPrize,
  });

  const isBlockedByPrizeValueLimit = (): boolean => {
    if (apiTicketing.formCategory !== TicketingFormCategory.Sweepstakes) {
      return false;
    }
    const totalPrizeValue = sumSweepstakesPrizeValues(form.getValues("prizes"));
    const restriction = getSweepstakesPrizeValueRestriction({
      country: organization?.country,
      region: organization?.region,
      organizationType: organization?.type,
      totalPrizeValue,
    });
    if (restriction.isRestricted) {
      setValueRestriction({ regionName: restriction.regionName ?? "", totalPrizeValue });
    }
    return restriction.isRestricted;
  };

  const handleNext = async () => {
    if (!isBlockedByPrizeValueLimit()) {
      await handleSaveAndNext();
    }
  };

  const handleBack = async () => {
    if (!isBlockedByPrizeValueLimit()) {
      await handleSaveAndBack();
    }
  };

  const focusPrizeInPreview = (prizeId: string) => {
    setPreviewPrizeFocusId(prizeId);
    scrollAndUpdateArrow();
  };

  const handleBackToDashboard = () => {
    form.reset();
    exitLiveFormEditor();
  };

  return (
    <StepPrizes
      form={form}
      prizeList={prizeList}
      onNext={handleNext}
      onBack={handleBack}
      valueRestriction={valueRestriction}
      onEditPrizes={() => setValueRestriction(null)}
      onBackToDashboard={handleBackToDashboard}
      onUploaderChange={handleUploaderChange}
      onPrizeActivate={focusPrizeInPreview}
      isSweepstakes={apiTicketing.formCategory === TicketingFormCategory.Sweepstakes}
    />
  );
};
